import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import { findIndex, findLastIndex } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { PanoramaDetails } from '../services/UploadManagementServiceTypes';

interface State {
  leftButtonVisible: boolean;
  rightButtonVisible: boolean;
  contentFits: boolean;
}

const getElementVisibilityProperties = (container: HTMLDivElement, element: Element) => {
  const { left, right } = container.getBoundingClientRect();
  const { left: elementLeft, right: elementRight } = element.getBoundingClientRect();
  const isLeftVisible = elementLeft >= left && elementLeft < right;
  const isRightVisible = elementRight > left && elementRight <= right;

  return {
    isLeftVisible,
    isRightVisible,
  };
};

const getNavigationVisibility = (container: HTMLDivElement): State => {
  const { clientWidth, scrollLeft, scrollWidth } = container;
  const isScrollbarLeft = scrollLeft === 0;
  const isScrollbarRight = scrollWidth - clientWidth - scrollLeft === 0;
  const contentFits = scrollWidth === clientWidth;
  return {
    leftButtonVisible: !isScrollbarLeft,
    rightButtonVisible: !isScrollbarRight,
    contentFits,
  };
};

export interface PanoramaThumbnailViewerProps {
  children: (p: {
    thumbnailContainerRef: React.MutableRefObject<HTMLDivElement | null>;
    contentFits: boolean;
  }) => React.ReactElement;
  panoramas: PanoramaDetails[];
  currentPanoramaIndex: number;
}
// important: don't use a class selector, it won't work on PROD anymore
const getThumbnails = (container: HTMLDivElement) => Array.from(container.children);

export const PanoramaThumbnailViewer: React.FunctionComponent<PanoramaThumbnailViewerProps> = ({
  children,
  panoramas,
  currentPanoramaIndex,
}) => {
  const [state, setState] = useState<State>({
    leftButtonVisible: false,
    rightButtonVisible: false,
    contentFits: false,
  });
  const thumbnailContainerRef = useRef<HTMLDivElement | null>(null);

  const styles = useStyles(state);

  useEffect(() => {
    const container = thumbnailContainerRef.current;
    if (!container) {
      return;
    }

    const setVisibility = () => {
      setState((prevState) => ({ ...prevState, ...getNavigationVisibility(container) }));
    };

    container.addEventListener('scroll', setVisibility);
    setVisibility();

    return () => {
      container.removeEventListener('scroll', setVisibility);
    };
  }, [thumbnailContainerRef, setState, panoramas]);

  useEffect(() => {
    const container = thumbnailContainerRef.current;
    if (!container || currentPanoramaIndex === -1) {
      return;
    }
    const thumbnails = getThumbnails(container);
    const nextThumbnail = thumbnails[currentPanoramaIndex];
    nextThumbnail?.scrollIntoView({ behavior: 'smooth' });
  }, [thumbnailContainerRef, currentPanoramaIndex]);

  const onGoRight = () => {
    const container = thumbnailContainerRef.current;
    if (!container) {
      return;
    }
    const thumbnails = getThumbnails(container);
    const visibleProperties = thumbnails.map((i) => getElementVisibilityProperties(container, i));
    const lastVisibleImageIndex = findLastIndex(
      visibleProperties,
      ({ isLeftVisible }) => isLeftVisible,
    );

    const scrollToIndex =
      lastVisibleImageIndex === thumbnails.length - 1
        ? lastVisibleImageIndex
        : lastVisibleImageIndex + 1;
    const nextThumbnail = thumbnails[scrollToIndex];
    nextThumbnail?.scrollIntoView({ behavior: 'smooth' });
  };

  const onGoLeft = () => {
    const container = thumbnailContainerRef.current;
    if (!container) {
      return;
    }
    const thumbnails = getThumbnails(container);
    const visibleProperties = thumbnails.map((i) => getElementVisibilityProperties(container, i));
    const lastVisibleImageIndex = findIndex(
      visibleProperties,
      ({ isRightVisible }) => isRightVisible,
    );

    const scrollToIndex =
      lastVisibleImageIndex === 0 ? lastVisibleImageIndex : lastVisibleImageIndex - 1;
    const nextThumbnail = thumbnails[scrollToIndex];
    nextThumbnail?.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <Box className={styles.container}>
      <button
        type='button'
        className={`${styles.navigationButton} ${styles.navigationLeft}`}
        onClick={onGoLeft}
      >
        <ChevronLeft />
      </button>
      <Box className={styles.thumbnailContainer}>
        {children({ thumbnailContainerRef, contentFits: state.contentFits })}
      </Box>
      <button
        type='button'
        className={`${styles.navigationButton} ${styles.navigationRight}`}
        onClick={onGoRight}
      >
        <ChevronRight />
      </button>
    </Box>
  );
};

const useStyles = makeStyles<Theme, State>(() =>
  createStyles({
    container: {
      display: 'flex',
      backgroundColor: 'rgba(51, 51, 74, 0.8)',
      width: '100%',
      padding: '10px',
      justifyContent: 'space-between',
      height: '200px',
      boxSizing: 'border-box',
    },
    thumbnailContainer: {
      flexGrow: 1,
      overflow: 'hidden',
    },
    navigationButton: {
      margin: '3px',
      backgroundColor: 'rgba(51, 51, 74, 1)',
      color: 'white',
      '& svg': {
        fontSize: 40,
      },
    },
    navigationLeft: {
      visibility: ({ leftButtonVisible }) => (leftButtonVisible ? 'visible' : 'hidden'),
    },
    navigationRight: {
      visibility: ({ rightButtonVisible }) => (rightButtonVisible ? 'visible' : 'hidden'),
    },
  }),
);
